import {
  DeleteOutlined,
  EditOutlined,
  LayoutOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import {
  Button,
  message,
  Modal,
  Popconfirm,
  Space,
  SpinProps,
  Table,
  useForm,
} from "@pankod/refine-antd";
import { OpenNotificationParams, useUpdate } from "@pankod/refine-core";
import { DrawerAddTemplate } from "components/drawers";
import { ITemplate } from "interfaces";
import { useMemo, useState } from "react";

interface IParametrizeTempateProps {
  campaignId: string;
  dataSource: ITemplate[];
  loading?: boolean | SpinProps;
}

export function ModalParametrizeTemplates({
  campaignId,
  dataSource,
  loading,
}: IParametrizeTempateProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { formProps, queryResult } = useForm<ITemplate>();
  const [{ currentPage, pageSize }, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
  });

  const paginatedData = useMemo(() => {
    return dataSource.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [currentPage, pageSize, dataSource]);

  const { mutate: updateTemplates, isLoading: isUpdating } = useUpdate();

  const defaultMutateOptions: any = {
    resource: "v1/repique-campaign",
    id: campaignId,
    successNotification: {
      type: "success" as OpenNotificationParams["type"],
      description: "Sucesso",
      message: "Operação realizada com sucesso",
    },
    errorNotification: (error: any) => ({
      type: "error",
      description: "Erro!",
      message: error?.response?.data?.detail ?? "Erro tentar executar operação",
    }),
  };

  const onRemove = (index: number) => {
    if (editingIndex !== null) {
      message.warning(
        "Existe um template em ajuste. Salve ou cancele antes de remover."
      );
      return;
    }

    const templates = [...dataSource];

    templates.splice(index, 1);
    updateTemplates({
      ...defaultMutateOptions,
      values: { templates },
    });
  };

  const onEdit = (index: number) => {
    if (editingIndex !== null) {
      message.warning(
        "Existe um template em ajuste. Salve ou cancele antes de editar outro."
      );
      return;
    }

    const currentValues = dataSource[index];

    formProps.form?.setFieldsValue(currentValues);

    setEditingIndex(index);
    setOpenDrawer(true);
  };

  const handleSaveEdit = (editedValues: ITemplate) => {
    if (editingIndex === null) return;

    const templates = [...dataSource];

    templates.splice(editingIndex, 1, {
      ...dataSource[editingIndex],
      ...editedValues,
    });

    updateTemplates({
      ...defaultMutateOptions,
      values: { templates },
    });

    setEditingIndex(null);
    setOpenDrawer(false);
    formProps.form?.resetFields();
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setEditingIndex(null);
    formProps.form?.resetFields();
    formProps.form?.setFieldsValue({});
  };

  const handleCreate = (template: ITemplate) => {
    const templates = [...dataSource, template];

    updateTemplates({
      ...defaultMutateOptions,
      values: { templates },
    });

    setOpenDrawer(false);
    formProps.form?.resetFields();
  };

  return (
    <>
      <DrawerAddTemplate
        title={editingIndex !== null ? "Editar Template" : "Novo Template"}
        onFinish={(values) => {
          editingIndex !== null ? handleSaveEdit(values) : handleCreate(values);
        }}
        onClose={handleDrawerClose}
        open={openDrawer}
        formProps={formProps}
        queryResult={queryResult}
      />

      <Button
        size="small"
        icon={<LayoutOutlined />}
        onClick={() => setIsOpen(true)}
      >
        Templates
      </Button>

      <Modal
        visible={isOpen}
        title="Templates"
        footer={null}
        width="100%"
        style={{ maxWidth: "1157px" }}
        onCancel={() => setIsOpen(false)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
            padding: "10px",
          }}
        >
          <Space style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
            <h2 style={{ fontSize: "20px" }}>
              Configure os parâmetros dos templates
            </h2>

            <Button
              type="primary"
              icon={<PlusSquareOutlined />}
              onClick={() => setOpenDrawer(true)}
            >
              Adicionar
            </Button>
          </Space>

          <div>
            <Table
              dataSource={paginatedData}
              rowKey={(_, index = 0) =>
                `${(currentPage - 1) * pageSize + index}`
              }
              scroll={{ x: "max-content" }}
              loading={loading || isUpdating}
              pagination={{
                current: currentPage,
                pageSize,
                total: dataSource.length,
                onChange: (page, size) =>
                  setPagination({ currentPage: page, pageSize: size }),
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            >
              <Table.Column dataIndex="name" title="Nome do Template" />

              <Table.Column dataIndex="order" title="Ordem de Disparo" />

              <Table.Column dataIndex="last_screen" title="Última Tela" />

              <Table.Column
                dataIndex="abandon_minutes"
                title="Tempo de Abandono"
              />

              <Table.Column
                title="Ações"
                render={(_, __, index) => (
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() =>
                        onEdit((currentPage - 1) * pageSize + index)
                      }
                    />
                    <Popconfirm
                      title="Tem certeza que deseja remover este item?"
                      okText="Sim"
                      cancelText="Não"
                      onConfirm={() =>
                        onRemove((currentPage - 1) * pageSize + index)
                      }
                    >
                      <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )}
              />
            </Table>
          </div>
        </div>
      </Modal>
    </>
  );
}
